import React from 'react';
import { Alert, Box, Button, IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faLocationDot, faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { Standort, StandortTyp } from 'apiClient/Api';
import { DEFAULT_STANDORT_ID, STANDORT_TYP_BESCHREIBUNG, STANDORT_TYP_LABEL } from './instanceModel';

const newId = (): string =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID().slice(0, 8) : `s${Date.now()}`;

/**
 * Vorgeschaltete Standort-/Bereichs-Definition. Es gibt immer den Standard-Standort „A"
 * (umbenennbar, nicht löschbar). Weitere Standorte sind Stand-Alone (eigene Zentraltechnik)
 * oder Nur-Funk (nur Funk-Interface, hängt an einem Stand-Alone-Standort).
 */
export const StandortManager: React.FC<{ standorte: Standort[]; onChange: (next: Standort[]) => void }> = ({
    standorte,
    onChange,
}) => {
    // Mögliche Bezugs-Standorte je Typ:
    //  • Versorgt (Child)   → von einem Stand-Alone-Standort (mit Zentraltechnik) versorgt
    //  • Verbunden (Connected) → mit einem Stand-Alone ODER einem anderen Verbunden-Standort verbunden
    const parentCandidates = (s: Standort): Standort[] =>
        standorte.filter(
            (o) =>
                o.id !== s.id &&
                (o.typ === StandortTyp.StandAlone ||
                    (s.typ === StandortTyp.Connected && o.typ === StandortTyp.Connected)),
        );

    const update = (id: string, patch: Partial<Standort>) =>
        onChange(standorte.map((s) => (s.id === id ? { ...s, ...patch } : s)));

    const add = () =>
        onChange([...standorte, { id: newId(), name: '', typ: StandortTyp.StandAlone, gehoertZu: null }]);

    const remove = (id: string) => onChange(standorte.filter((s) => s.id !== id));

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                <FontAwesomeIcon icon={faLocationDot} />
                <Typography variant="h6">Bereich / Standort</Typography>
            </Stack>
            <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Lege die Standorte an, die mit <b>Reichert-Technik</b> ausgestattet werden — nur diese werden
                    berücksichtigt. Die Konfiguration erfolgt anschließend je Standort.
                </Typography>
                <Stack spacing={0.75}>
                    {[StandortTyp.StandAlone, StandortTyp.Child, StandortTyp.Connected].map((typ) => (
                        <Typography key={typ} variant="body2">
                            <b>{STANDORT_TYP_LABEL[typ]}:</b> {STANDORT_TYP_BESCHREIBUNG[typ]}
                        </Typography>
                    ))}
                </Stack>
            </Alert>

            <Stack spacing={1.5}>
                {standorte.map((s) => {
                    const isDefault = s.id === DEFAULT_STANDORT_ID;
                    const needsParent = s.typ === StandortTyp.Child || s.typ === StandortTyp.Connected;
                    return (
                        <Stack
                            key={s.id}
                            direction="row"
                            spacing={1}
                            useFlexGap
                            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                        >
                            <TextField
                                size="small"
                                label="Name"
                                value={s.name || ''}
                                onChange={(e) => update(s.id!, { name: e.target.value })}
                                placeholder={isDefault ? 'Standard' : 'z. B. Schlauchturm'}
                                sx={{ flexGrow: 1, minWidth: 200 }}
                            />
                            <TextField
                                select
                                size="small"
                                label="Typ"
                                value={s.typ || StandortTyp.StandAlone}
                                onChange={(e) => {
                                    const typ = e.target.value as StandortTyp;
                                    const stillNeedsParent = typ === StandortTyp.Child || typ === StandortTyp.Connected;
                                    update(s.id!, { typ, gehoertZu: stillNeedsParent ? s.gehoertZu ?? null : null });
                                }}
                                sx={{ width: 200 }}
                            >
                                <MenuItem value={StandortTyp.StandAlone}>
                                    {STANDORT_TYP_LABEL[StandortTyp.StandAlone]} (eigene Zentraltechnik)
                                </MenuItem>
                                <MenuItem value={StandortTyp.Child}>
                                    {STANDORT_TYP_LABEL[StandortTyp.Child]} (von Standort)
                                </MenuItem>
                                <MenuItem value={StandortTyp.Connected}>
                                    {STANDORT_TYP_LABEL[StandortTyp.Connected]} (mit Standort)
                                </MenuItem>
                            </TextField>
                            <Tooltip title={STANDORT_TYP_BESCHREIBUNG[(s.typ as StandortTyp) || StandortTyp.StandAlone]}>
                                <Box component="span" sx={{ color: 'text.secondary', display: 'inline-flex' }}>
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                </Box>
                            </Tooltip>
                            {needsParent && (
                                <TextField
                                    select
                                    size="small"
                                    label={s.typ === StandortTyp.Child ? 'versorgt von' : 'verbunden mit'}
                                    value={s.gehoertZu ?? ''}
                                    onChange={(e) => update(s.id!, { gehoertZu: e.target.value || null })}
                                    sx={{ width: 200 }}
                                    error={!s.gehoertZu}
                                >
                                    {parentCandidates(s).map((o) => (
                                        <MenuItem key={o.id} value={o.id}>
                                            {o.name || o.id}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                            <Box sx={{ flexGrow: 1 }} />
                            <Tooltip title={isDefault ? 'Standard-Standort kann nicht gelöscht werden' : 'Standort löschen'}>
                                <span>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={isDefault}
                                        onClick={() => remove(s.id!)}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    );
                })}
            </Stack>

            <Button sx={{ mt: 1 }} startIcon={<FontAwesomeIcon icon={faPlus} />} onClick={add}>
                Standort hinzufügen
            </Button>
        </Paper>
    );
};
